<script setup lang="ts">
  import {
    Modal,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import {
    IconSettings,
    IconAccount,
    IconGlobe,
    IconSupport,
    IconClose
  } from '@gat-ship/icons'
  import SettingsPanelAccount from './SettingsPanelAccount.vue'
  import SettingsPanelGeneral from './SettingsPanelGeneral.vue'
  import SettingsPanelLocalization from './SettingsPanelLocalization.vue'
  import SettingsPanelSupport from './SettingsPanelSupport.vue'
  import { useSettingsTabs } from '../composables/useSettingsTabs'

  // Open-state, active tab, and the mobile tab-bar classes come from this
  // seam, which sources them from the central ordered stack (the `?modals=`
  // codec owns the URL) and the pure tab decisions.
  const { open, activeItem, tabClass } = useSettingsTabs()

  // Overrides on top of sidebarMenuButtonVariants to match the previous
  // ModalSidebarNavItem styling (auto height, muted inactive text).
  // ring-inset: SidebarContent scrolls (overflow-auto), which would clip
  // a ring drawn outside the button bounds.
  const menuButtonClass =
    'text-muted-foreground hover:bg-accent/50 hover:text-foreground data-[active=true]:hover:bg-accent data-[active=true]:hover:text-accent-foreground h-auto cursor-pointer px-3 py-2 focus-visible:ring-inset'
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
  >
    <SidebarProvider
      class="h-full min-h-0 w-full overflow-hidden md:h-[min(800px,85vh)] md:rounded-lg"
    >
      <Sidebar
        collapsible="none"
        class="border-border bg-muted/50 hidden w-[220px] shrink-0 border-r p-3 dark:bg-neutral-800/50 md:flex"
      >
        <SidebarHeader class="text-foreground px-3 py-2 text-sm font-semibold">
          Settings
        </SidebarHeader>
        <SidebarContent class="gap-1">
          <SidebarGroup class="p-0">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    :is-active="activeItem === 'general'"
                    :class="menuButtonClass"
                    @click="activeItem = 'general'"
                  >
                    <IconSettings />
                    <span>General</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    :is-active="activeItem === 'account'"
                    :class="menuButtonClass"
                    @click="activeItem = 'account'"
                  >
                    <IconAccount />
                    <span>Account</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    :is-active="activeItem === 'localization'"
                    :class="menuButtonClass"
                    @click="activeItem = 'localization'"
                  >
                    <IconGlobe />
                    <span>Localization</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    :is-active="activeItem === 'support'"
                    :class="menuButtonClass"
                    @click="activeItem = 'support'"
                  >
                    <IconSupport />
                    <span>Support</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <SidebarInset class="overflow-hidden">
        <div class="relative z-10 shrink-0">
          <div class="bg-background flex items-center px-3 py-1.5">
            <div class="min-w-0 flex-1">
              <span
                class="text-foreground pl-1 text-sm font-semibold md:hidden"
              >
                Settings
              </span>
            </div>
            <button
              type="button"
              class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
              @click="open = false"
            >
              <IconClose class="size-4" />
              <span class="sr-only">Close</span>
            </button>
          </div>
          <nav
            class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
          >
            <button
              type="button"
              :class="tabClass('general')"
              @click="activeItem = 'general'"
            >
              <IconSettings class="size-4" />
              <span>General</span>
            </button>
            <button
              type="button"
              :class="tabClass('account')"
              @click="activeItem = 'account'"
            >
              <IconAccount class="size-4" />
              <span>Account</span>
            </button>
            <button
              type="button"
              :class="tabClass('localization')"
              @click="activeItem = 'localization'"
            >
              <IconGlobe class="size-4" />
              <span>Localization</span>
            </button>
            <button
              type="button"
              :class="tabClass('support')"
              @click="activeItem = 'support'"
            >
              <IconSupport class="size-4" />
              <span>Support</span>
            </button>
          </nav>
          <div
            class="bg-background pointer-events-none h-3"
            style="mask-image: linear-gradient(to bottom, black, transparent)"
          />
        </div>
        <div class="-mt-3 flex flex-1 flex-col overflow-y-auto">
          <SettingsPanelGeneral v-if="activeItem === 'general'" />
          <SettingsPanelAccount v-else-if="activeItem === 'account'" />
          <SettingsPanelLocalization
            v-else-if="activeItem === 'localization'"
          />
          <SettingsPanelSupport v-else-if="activeItem === 'support'" />
        </div>
      </SidebarInset>
    </SidebarProvider>
  </Modal>
</template>
